import { useId, useMemo, useState, useEffect } from 'react';

import { blurActiveElement } from '@/utils';

const SELECT_ALL_THRESHOLD = 5;

const PlaylistPicker = ({
  target,
  disabled,
  isLoading,
  playlists,
  isComplete,
  isConverting,
  onGetPlaylists,
  selectedIds = [],
  onSelectionChange,
  label = 'Select playlists to transfer',
}) => {
  const masterId = useId();

  const [collapsed, setCollapsed] = useState(false);

  // Reset UI when target changes (only if not complete)
  useEffect(() => {
    if (!isComplete) {
      setCollapsed(false);
    }
  }, [target, isComplete]);

  // Auto-collapse once conversion starts (free up space)
  useEffect(() => {
    if (isConverting) {
      setCollapsed(true);
    }
  }, [isConverting]);

  // Auto-collapse once conversion completes
  useEffect(() => {
    if (isComplete) {
      setCollapsed(true);
    }
  }, [isComplete]);

  // Fast lookups
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Toggle a single playlist (controlled)
  const toggle = (playlistId) => {
    blurActiveElement();

    const next = new Set(selectedSet);

    next.has(playlistId) ? next.delete(playlistId) : next.add(playlistId);

    onSelectionChange(Array.from(next));
  };

  // Select or deselect all playlists (controlled)
  const handleSelectAll = () => {
    blurActiveElement();

    if (!playlists?.length) return;

    if (selectedSet.size === playlists.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(playlists.map((playlist) => playlist.id));
    }
  };

  // Determine checkbox states
  const noneSelected = selectedSet.size === 0;
  const allSelected = selectedSet.size === playlists?.length;

  const indeterminate = !noneSelected && !allSelected;

  const isScrollable = playlists?.length > SELECT_ALL_THRESHOLD;

  const selectAllTopId = `${masterId}SelectAllTop`;
  const selectAllBottomId = `${masterId}SelectAllBottom`;

  return playlists ? (
    <div style={{ width: '100%' }}>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className={`form-label mb-0 ${disabled ? 'text-muted' : ''}`}>
          {label}
          {collapsed && (
            <small className="text-muted ms-2">
              ({selectedIds.length})
            </small>
          )}
        </label>

        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((c) => !c)}
          className="btn btn-link p-0 text-accent"
          aria-controls={`${masterId}-pickerBody`}
        >
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {/* Collapsible body */}
      {!collapsed && (
        <div
          id={`${masterId}-pickerBody`}
          style={{
            ...(isScrollable ? { maxHeight: '143px', overflowY: 'auto' } : {}),
          }}
        >
          {/* Select All at Top */}
          {isScrollable && !disabled && (
            <div className="form-check">
              <input
                type="checkbox"
                id={selectAllTopId}
                disabled={disabled}
                checked={allSelected}
                onChange={handleSelectAll}
                className="form-check-input"
                ref={(el) => el && (el.indeterminate = indeterminate)}
              />
              <label
                htmlFor={selectAllTopId}
                className="form-check-label text-accent"
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </label>
            </div>
          )}

          {/*Todo when playlists is empty indicate it to the user*/}
          {/* Playlist Options */}
          <div>
            {playlists.map(({ id, name }) => {
              const checkId = `${masterId}-${id}`;

              return (
                <div key={id} className="form-check">
                  <input
                    id={checkId}
                    type="checkbox"
                    disabled={disabled}
                    onChange={() => toggle(id)}
                    className="form-check-input"
                    checked={selectedSet.has(id)}
                  />
                  <label htmlFor={checkId} className="form-check-label">
                    {name.trim()}
                  </label>
                </div>
              );
            })}
          </div>

          {/* Select All at Bottom */}
          {isScrollable && !disabled && (
            <div className="form-check">
              <input
                type="checkbox"
                disabled={disabled}
                checked={allSelected}
                id={selectAllBottomId}
                onChange={handleSelectAll}
                className="form-check-input"
                ref={(el) => el && (el.indeterminate = indeterminate)}
              />
              <label
                htmlFor={selectAllBottomId}
                className="form-check-label text-accent"
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  ) : disabled && !isLoading ? null : (
    <>
      <div>
        <button
          type="button"
          disabled={isLoading}
          onClick={onGetPlaylists}
          className="btn btn-outline-custom btn-outline-accent"
        >
          Load playlists
        </button>

        {isLoading && (
          <small className="text-accent ms-3">
            <i
              aria-hidden="true"
              style={{ fontSize: '0.75rem' }}
              className="fas fa-spinner fa-spin me-1"
            ></i>
            <span>Loading...</span>
          </small>
        )}
      </div>
    </>
  );
};

export default PlaylistPicker;
